/**
 * Sheet — bottom sheet overlay with panel.
 *
 * H5 source: `.sheet-overlay` (styles.css:2409–2420) + `.sheet-panel` (styles.css:2432–2473)
 * Safe-area: bottom padding via `--safe-bottom` / `--keyboard-inset`.
 */

/**
 * Strategy A PR-9: switched to H5 globals. `.overlay.sheet-overlay` carries
 * the backdrop + sticky 480px frame; `.sheet-panel` is the inner card.
 * Modifiers: `.is-open` toggles visibility + animation, `.is-compose-fullscreen`
 * stretches the panel for ComposeSheetHost full-screen mode.
 *
 * Audit B-1 (docs/plans/a11y-deep-audit-2026-05-20.md): keyboard + focus
 * contract is now centralized here. Every Sheet host gets:
 *   - Escape → onClose (window keydown, cleaned up on unmount). Opt out via
 *     closeOnEscape={false} when an inner element should swallow Escape
 *     (e.g. confirm-style sheets that already gate it).
 *   - On open: auto-focus the first interactive element in the panel so SR /
 *     keyboard users land inside the dialog instead of being stranded behind
 *     the backdrop. Falls back to the panel itself (tabIndex=-1).
 *   - On close: restore focus to whatever element triggered the open. Without
 *     this, dismissing a sheet with the keyboard left focus on <body>.
 *
 * Audit B-5 (focus trap): Tab cycles within the panel by default. Shift+Tab
 * on the first focusable wraps to the last; Tab on the last wraps to the
 * first. Opt out via trapFocus={false} for sheets that intentionally allow
 * keyboard navigation outside (rare). The trap is gated on manageFocus so
 * sheets that opt out of focus management get no trap either.
 */

import {
  useCallback,
  useEffect,
  useRef,
  type MouseEvent,
  type ReactNode,
} from "react";

// Audit B-1 / B-5: shared selector for both auto-focus on open and the
// Tab/Shift+Tab trap. Excludes [tabindex="-1"] so the panel itself
// (which carries tabIndex={-1}) doesn't appear in the cycle.
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface SheetProps {
  children: ReactNode;
  /** Whether the sheet is visible. */
  open: boolean;
  /** Full-screen mode (maps to `.is-compose-fullscreen`). */
  fullscreen?: boolean;
  /** Called when the backdrop (not panel) is clicked. */
  onClose?: () => void;
  /** Accessible label id for the inner dialog. */
  labelledBy?: string;
  /** Accessible label text for the inner dialog (when no labelledBy element). */
  label?: string;
  /**
   * H5 panel modifier appended to `.sheet-panel` so styles like
   * `.sheet-panel.compose-panel` (H5 styles.css) match. Pass `"compose-panel"`,
   * `"search-panel"`, etc. Without it the `<section>` only gets `.sheet-panel`
   * and H5 sheet-type-specific rules don't apply.
   *
   * For auth-overlay use, set `overlayKind="auth"` which switches the panel
   * to bare `.auth-panel` (H5 auth doesn't share `.sheet-panel` typography).
   */
  panelClass?: string;
  /**
   * H5 overlay variant. Different overlays have different positioning + z-index:
   *   - `"sheet"` (default) → `.overlay.sheet-overlay` (bottom-fixed, 480px)
   *   - `"auth"` → `.overlay.auth-overlay` (full-screen centered, z=110)
   *   - `"confirm"` → `.overlay.confirm-overlay` (centered modal, z=120)
   */
  overlayKind?: "sheet" | "auth" | "confirm";
  /**
   * Audit B-1: when true (default), pressing Escape while the sheet is
   * open invokes onClose. Pass false for sheets that need to handle
   * Escape themselves (e.g. nested confirm-style flows).
   */
  closeOnEscape?: boolean;
  /**
   * Audit B-1: when true (default), the sheet auto-focuses its first
   * interactive child on open and restores focus to the previously
   * focused element on close. Pass false to opt out — e.g. for sheets
   * that programmatically manage focus elsewhere.
   */
  manageFocus?: boolean;
  /**
   * Audit B-5: when true (default), Tab / Shift+Tab cycle within the
   * panel so keyboard focus never escapes back to the page beneath
   * the sheet. Implicitly disabled when manageFocus={false}.
   */
  trapFocus?: boolean;
}

export function Sheet({
  children,
  open,
  fullscreen = false,
  onClose,
  labelledBy,
  label,
  panelClass,
  overlayKind = "sheet",
  closeOnEscape = true,
  manageFocus = true,
  trapFocus = true,
}: SheetProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  // Audit B-1: remember the element that triggered open so we can return
  // focus there on close (RULE 17 AP9: cleanup keyboard listeners).
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Audit B-1: window-level Escape listener, scoped to open=true so we
  // don't intercept Escape for inactive sheets stacked off-screen.
  useEffect(() => {
    if (!open || !closeOnEscape || !onClose) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // Stop the event so a nested host (e.g. another modal) doesn't
      // double-handle, but only when we actually fire onClose.
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, closeOnEscape, onClose]);

  // Audit B-1: auto-focus on open + restore on close. The cleanup runs
  // on close (open transitions true → false) AND on unmount; both should
  // bounce focus back to the trigger so keyboard users don't end up on
  // <body>.
  useEffect(() => {
    if (!open || !manageFocus) return undefined;
    if (typeof document === "undefined") return undefined;
    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    if (panel) {
      const firstFocusable = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstFocusable ?? panel).focus({ preventScroll: true });
    }
    return () => {
      const prev = previouslyFocusedRef.current;
      if (prev && document.contains(prev)) {
        prev.focus({ preventScroll: true });
      }
      previouslyFocusedRef.current = null;
    };
  }, [open, manageFocus]);

  // Audit B-5: Tab / Shift+Tab focus trap. Bound to the panel, not the
  // window, so the trap only fires when focus is already inside the
  // sheet — outer keyboard navigation (e.g. browser chrome) is
  // unaffected. Implicitly off when manageFocus={false}.
  useEffect(() => {
    if (!open || !manageFocus || !trapFocus) return undefined;
    const panel = panelRef.current;
    if (!panel) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusables.length === 0) {
        // Nothing focusable inside — keep focus on the panel itself.
        event.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else if (active === last || !panel.contains(active)) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };
    panel.addEventListener("keydown", handleKeyDown);
    return () => panel.removeEventListener("keydown", handleKeyDown);
  }, [open, manageFocus, trapFocus]);

  const handleBackdropClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target === overlayRef.current && onClose) {
        onClose();
      }
    },
    [onClose],
  );

  const overlayCls = [
    "overlay",
    `${overlayKind}-overlay`,
    open ? "is-open" : "",
    fullscreen ? "is-compose-fullscreen" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const defaultPanelBase = overlayKind === "auth" ? "" : "sheet-panel";
  // H5 main.js:3489 toggles `.is-fullscreen` on `.compose-panel` in sync with
  // `.is-compose-fullscreen` on the overlay (see UX-5.3 fullscreen wiring).
  // Mirror by adding both class layers from a single `fullscreen` prop so the
  // panel-level rules (`.compose-panel.is-fullscreen`, styles.css:3709) apply.
  const panelCls = [
    defaultPanelBase,
    panelClass ?? "",
    fullscreen ? "is-fullscreen" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // P1-B (audit §L5): mirror the `.is-open` H5 class as a parallel
  // `data-state="open" | "closed"` attribute. Lets feature-side code (and
  // future CSS) drive transitions via `[data-state]` (the React-native
  // convention pioneered by Radix/Shadcn) without breaking the H5 visual
  // contract that still owns `.is-open` for the static stylesheet. Keeps
  // RULE 17 AP2 spirit: state lives in React props, not classlist mutated
  // post-rAF.
  const dataState = open ? "open" : "closed";

  return (
    <div
      ref={overlayRef}
      className={overlayCls}
      data-state={dataState}
      onClick={handleBackdropClick}
      role="presentation"
    >
      <section
        ref={panelRef}
        className={panelCls}
        data-state={dataState}
        role="dialog"
        aria-modal={open}
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : label}
        tabIndex={-1}
      >
        {children}
      </section>
    </div>
  );
}
